import styled from "styled-components";
import { FilterInput } from "./input";
import { IFilterInput, KeyValueObj } from "@/common/types";
import { useCallback, useMemo, useState } from "react";
import { DateInput } from "./date-input";
import { FilterSelectInput } from "./filter-select-input";
import { Button } from "../../button";
import { H3 } from "@/common/elements";
import { validateRangeFilters } from "./validate-range";

const FilterWrapper = styled.div`
  width: 32rem;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 4rem 2.1rem;
  gap: 2rem;
  background-color: ${({ theme }) => theme.bg.prim_500};

  .inputs {
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;
    gap: 2rem 1rem;
    align-items: center;
  }

  .controls {
    width: 100%;
    display: flex;
    /* justify-content: space-between; */
    gap: 10px;
    padding: 1rem 2rem;
    align-self: center;
    justify-content: center;
  }

  .active-filters {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .active-filters-label {
      font-size: 0.75rem;
      font-weight: 600;
      letter-spacing: 0.03em;
      text-transform: uppercase;
      color: ${({ theme }) => theme.text.prim_100};
    }

    .active-filters-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
    }

    .active-filter-chip {
      font-size: 0.75rem;
      color: ${({ theme }) => theme.bg.secondary};
      background-color: ${({ theme }) => theme.bg.sec_400};
      border-radius: 999px;
      padding: 0.3rem 0.75rem;

      strong {
        font-weight: 600;
      }
    }
  }
`;

export interface IFilterProps {
  onFilter?: (formValues: KeyValueObj) => void;
  filterInputs: IFilterInput[];
  // The currently-applied filter values, so reopening shows what's actually
  // active instead of blank fields. Safe to seed straight into state: this
  // component fully unmounts on close (Modal returns null when !isOpen), so
  // a fresh mount always picks up whatever is current at that moment.
  initialValues?: KeyValueObj;
}

export function Filter({ onFilter, filterInputs, initialValues }: IFilterProps) {
  const [formValues, setFormValues] = useState<KeyValueObj>(
    initialValues ?? {},
  );

  // Clearing must also re-apply the (now empty) filter set — resetting only
  // the local form state left the fields looking cleared while the
  // previously-applied filters stayed in effect until Filter was clicked
  // again, which reads as "Clear does nothing".
  const clearFormValues = useCallback(() => {
    const clearedValues: KeyValueObj = {};
    Object.keys(formValues).forEach((formKey: string) => {
      clearedValues[formKey] = "";
    });
    setFormValues(clearedValues);
    onFilter?.(clearedValues);
  }, [setFormValues, formValues, onFilter]);

  // MED-713: an inverted range (min price > max price, expiry-from later than
  // expiry-to, and any other range filter declared on the screen) must not be
  // applied. The message is shown at the field and the Filter button is blocked
  // until it's corrected.
  const rangeErrors = useMemo(
    () => validateRangeFilters(filterInputs, formValues),
    [filterInputs, formValues],
  );
  const hasRangeErrors = Object.keys(rangeErrors).length > 0;

  // One chip per filled-in field, shown above the buttons so it's clear what
  // is (or is about to be, once Filter is clicked) applied — resolves a
  // radio/select's raw value (e.g. "in_stock") to its label ("In Stock").
  const activeFilters = filterInputs
    ?.map((input) => {
      const rawValue = formValues[input.name];
      if (!rawValue) return null;

      const value =
        input.options?.find((option) => option.value === rawValue)?.label ??
        rawValue;

      return { name: input.name, label: input.label, value };
    })
    .filter((entry): entry is { name: string; label: string; value: string } => entry !== null);

  return (
    <FilterWrapper>
      <div className="text flex flex-col align-middle">
        <H3>Filter By: </H3>
      </div>
      <div className="inputs">
        {filterInputs?.map((input: IFilterInput, i: number) =>
          input.type === "text" ? (
            <FilterInput
              key={i}
              indexLevel={1000 - i}
              label={input.label}
              setFormValues={setFormValues}
              name={input.name}
              formValues={formValues}
              error={rangeErrors[input.name]}
            />
          ) : input.type === "date" ? (
            <DateInput
              name={input.name}
              setFormValues={setFormValues}
              formValues={formValues}
              label={input.label}
              key={i}
              indexLevel={1000 - i}
              error={rangeErrors[input.name]}
            />
          ) : input.type === "radio" ? (
            <FilterSelectInput
              name={input.name}
              setFormValues={setFormValues}
              formValues={formValues}
              label={input.label}
              key={i}
              indexLevel={1000 - i}
              options={input.options}
            />
          ) : input.type === "select" ? (
            <FilterSelectInput
              name={input.name}
              setFormValues={setFormValues}
              formValues={formValues}
              label={input.label}
              key={i}
              indexLevel={1000 - i}
              options={input.options}
            />
          ) : null,
        )}
      </div>
      {activeFilters && activeFilters.length > 0 && (
        <div className="active-filters">
          <span className="active-filters-label">Active Filters</span>
          <div className="active-filters-chips">
            {activeFilters.map((filter) => (
              <span key={filter.name} className="active-filter-chip">
                <strong>{filter.label}:</strong> {filter.value}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="controls">
        <Button secondary onClick={clearFormValues}>
          Clear
        </Button>
        <Button
          disabled={hasRangeErrors}
          onClick={() => {
            if (hasRangeErrors) return;
            onFilter?.(formValues);
          }}
        >
          Filter
        </Button>
      </div>
    </FilterWrapper>
  );
}
